import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/AddUipps.css';
import FormContainer from '../CustomElements/FormContainer';
import CustomContainer from '../CustomElements/CustomContainer';
import CustomButton from '../CustomElements/CustomButton';
import Input from '../CustomElements/Input';
import Selector from '../CustomElements/Selector';
import DateSelector from '../CustomElements/DateSelector';
import CustomTextarea from '../CustomElements/CustomTextarea';
import PhoneInput from '../CustomElements/PhoneInput';
import CustomTitle from '../CustomElements/CustomTitle';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
// Кастомные хуки
import { useButtonValidation } from '../../customHook/useButtonValidation';
// Утилиты
import { handleUniversalChange } from '../../utils/formUtils';
import { isValidDeviceID } from '../../utils/isValidDeviceID';
import { isValidAndroidID } from '../../utils/isValidAndroidID';
// Действия Redux
import { addError } from '../../redux/actions//error.action';
import { initUippsAndroidIdFromServer } from '../../redux/actions/uipps.action';
import { initFilesFromServer } from '../../redux/actions/files.action';
import { load, remove } from '../../redux/actions/loader.action';
import { newDeviceFromServer } from '../../redux/actions/devices.action';
import FilePicker from '../FileManager/FilePicker';
import { removeEmptyStrings } from '../../utils/removeEmptyStrings';
import { omitFields } from '../../utils/omitFields';
import { transformFieldsToMoscowStart } from '../../utils/convertToDate';
import { convertStringValuesToNumbers } from '../../utils/convertStringValuesToNumbers';

// Этот компонент отображает форму для добавления нового устройства УИППС
// и обрабатывает отправку данных на сервер.

function AddUipps({ closeModal, t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();

  // Получаем опции
  const optionsForSelect = useSelector((state) => state.options);

  // Получаем данные о процессе
  const loader = useSelector((state) => state.loader);

  // Состояния уведомления об успешном добавлении
  const [addSuccess, setAddSuccess] = useState(false);

  // состояние для необязательных полей
  const [optionalFields] = useState(['sim1', 'sim2', 'note']);

  // Состояние для формы
  const [formData, setFormData] = useState({
    id: '',
    receiveDate: '',
    storageId: '',
    receiverId: '',
    sim1: '',
    isBroken: '',
    typeId: 4,
    acceptanceCert: null,
    senderId: '',
    manufactureDate: '',
    sim2: '',
    note: '',
    androidId: '',
  });

  // Функция для проверки валидности формы
  const isButtonDisabled = useButtonValidation(formData, optionalFields);

  // Обработчик отправки формы
  const handlerSubmit = (event) => {
    event.preventDefault();
    // Проверяем валидность id
    if (!isValidDeviceID(formData.id, formData.typeId)) {
      return dispatch(addError({ error: t?.invalidDeviceIdError || 'Указан невалидный ID устройства' }));
    }
    // Проверяем валидность androidId, если он предоставлен
    if (formData?.androidId && !isValidAndroidID(formData.androidId)) {
      return dispatch(addError({ error: t?.invalidAndroidIdError || 'Указан невалидный ANDROID_ID' }));
    }
    //переводим в состояние загрузки
    dispatch(load());
    // Удаляем пустые строки
    const formWithoutEmptyStrings = removeEmptyStrings(formData);
    // Убираем ненужные поля из формы
    const formWithoutExcluded = omitFields(formWithoutEmptyStrings, ['acceptanceCertName']);
    // Преобразуем дату (если есть) в начало дня по Москве
    const formWithMoscowDate = transformFieldsToMoscowStart(formWithoutExcluded, ['manufactureDate', 'receiveDate']);
    // Преобразуем нужные строковые поля в числа
    const payload = convertStringValuesToNumbers(formWithMoscowDate, ['id']);
    // Отправляем данные на сервер через Redux
    dispatch(newDeviceFromServer(payload));
  };

  //отслеживаем выполнение запроса
  useEffect(() => {
    // Если нет ошибок, закрываем модальное окно
    if (!loader?.isLoading && loader?.isSuccess) {
      setAddSuccess(true);
    }
    // при закрытии компонента чистим стейт загрузки
    return () => {
      dispatch(remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loader?.isLoading, loader?.isSuccess]);

  // Закрывает модальное окно после успешного добавления нового устройства
  const handlerCloseModal = () => {
    closeModal();
    setAddSuccess(false);
  };

  // при сбросе даты производства  сбрасываем дату приемки
  useEffect(() => {
    setFormData((prev) => ({ ...prev, receiveDate: '' }));
  }, [formData.manufactureDate]);

  // отправляем запрос на получений списка androidId и документов при открытии формы
  useEffect(() => {
    dispatch(initUippsAndroidIdFromServer());
    dispatch(initFilesFromServer());
  }, [dispatch]);

  return (
    <CustomContainer>
      <CustomTitle titleText={t?.addUippsTitle || 'ДОБАВЛЕНИЕ НОВОГО УИППС'} />
      {addSuccess && (
        <ConfirmationPage
          confirmText={t?.successAddUipps || 'Новый УИППС добавлен!'}
          onConfirm={handlerCloseModal}
          addNew={t?.backToList || 'К списку устройств'}
        />
      )}
      {!addSuccess && (
        <FormContainer>
          <Input
            column="left"
            label={`${t?.uippsId} *` || 'ID УИППС *'}
            placeholder={t?.uippsIdPlaceholder || 'Введите ID УИППС'}
            onChange={(event) => handleUniversalChange(formData, setFormData, event, null, null, null, null, null, 6)}
            name="id"
            width="100%"
            height="var(--sadr-font-size-60)"
            maxLength={6}
          />
          <DateSelector
            column="left"
            width="100%"
            height="var(--sadr-font-size-60)"
            label={t?.labelManufactureDate || 'Дата производства *'}
            placeholder={t?.placeholderManufactureDate || 'Выберите дату производства'}
            selectedDate={formData.manufactureDate}
            setSelectedDate={(date) =>
              handleUniversalChange(formData, setFormData, null, 'manufactureDate', date, 'date')
            }
            name="manufactureDate"
            maxDate={new Date()}
          />
          <PhoneInput
            column="left"
            label="SIM1"
            name="sim1"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            value={formData.sim1}
          />
          <Selector
            column="left"
            options={optionsForSelect.employees}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'receiverId', selectedOption, 'select')
            }
            label={t?.labelReceiverName || 'ФИО принявшего *'}
            placeholder={t?.placeholderReceiverName || 'Выберите ФИО принявшего'}
            width="100%"
            height="var(--sadr-font-size-60)"
            name="receiverId"
            isMulti={false}
          />
          <Selector
            column="left"
            options={optionsForSelect?.condition}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'isBroken', selectedOption, 'select')
            }
            label={t?.labelCondition || 'Состояние *'}
            placeholder={t?.placeholderCondition || 'Не определено'}
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
            name="isBroken"
          />
          <FilePicker
            column="left"
            label={t?.labelAcceptanceCert || 'Акт приема-передачи *'}
            formData={formData}
            setFormData={setFormData}
            fieldName="acceptanceCertName"
            fieldIdName="acceptanceCert"
          />
          <Selector
            column="right"
            options={optionsForSelect?.uippsAndroidId}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'androidId', selectedOption, 'select')
            }
            label="ANDROID_ID *"
            placeholder={t?.placeholderSelectAndroidId || 'Выберите android_id'}
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
            name="androidId"
            optionText="Нет неавторизованных УИППС"
          />
          <DateSelector
            column="right"
            width="100%"
            height="var(--sadr-font-size-60)"
            label={`${t?.labelReceiveDate} *` || 'Дата приемки *'}
            placeholder={t?.selectAcceptanceDate || 'Выберите дату приемки'}
            selectedDate={formData.receiveDate}
            setSelectedDate={(date) => handleUniversalChange(formData, setFormData, null, 'receiveDate', date, 'date')}
            name="receiveDate"
            minDate={formData?.manufactureDate}
            maxDate={new Date()}
          />
          <PhoneInput
            column="right"
            label="SIM2"
            name="sim2"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            value={formData.sim2}
          />
          <Selector
            column="right"
            options={optionsForSelect.employees}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'senderId', selectedOption, 'select')
            }
            label={t?.labelSenderName || 'ФИО передавшего *'}
            placeholder={t?.placeholderSenderName || 'Выберите ФИО передавшего'}
            width="100%"
            height="var(--sadr-font-size-60)"
            name="senderId"
            isMulti={false}
          />
          <Selector
            column="right"
            options={optionsForSelect.storages}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'storageId', selectedOption, 'select')
            }
            label={t?.labelStorageLocation || 'Место хранения *'}
            placeholder={t?.placeholderStorageLocation || 'Выберите место хранения'}
            width="100%"
            height="var(--sadr-font-size-60)"
            name="storageId"
            isMulti={false}
          />

          <CustomTextarea
            column="right"
            label={t?.commentTitle || 'Комментарий'}
            placeholder={t?.placeholderComment || 'Оставьте комментарий'}
            type="textArea"
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            name="note"
            width="100%"
            value={formData.note}
            rowSpan={2}
          />

          <CustomButton
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            text={t?.addUippsButton || 'Добавить УИППС'}
            disabled={isButtonDisabled}
            isButton
            margin={'0 0 var(--sadr-font-size-20) 0'}
            onClick={handlerSubmit}
          />
        </FormContainer>
      )}
    </CustomContainer>
  );
}

export default AddUipps;
